import {
  useAddProductToWishlist,
  useAuth,
  useCart,
  useRemoveProductFromWishlist,
} from "@the-volte/svc-core-sdk";
import { ProductDetails } from "@the-volte/svc-core-sdk/lib/fragments/gqlTypes/ProductDetails";
import { ISelectedProductInput } from "@the-volte/svc-core-sdk/lib/helpers";
import { isEmpty } from "lodash";
import { NextPage } from "next";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";

import { siteUrl, ssrMode } from "@app/constants";
import { useDatalayer } from "@app/providers/DatalayerProvider";
import { useTikTokPixelContext } from "@app/providers/TikTokPixelProvider";
import { EditProductLink } from "@components/atoms";
import useLogin from "@hooks/useLogin";
import { decodeBase64Id } from "@utils/base64";
import {
  sendFacebookAddToCartEvent,
  sendFacebookViewContentEvent,
} from "@utils/facebook";
import { getAttributeValue } from "@utils/getAttributeValue";
import { extractSeoDescription } from "@utils/seo";
import { applyStringTransformRules } from "@utils/strings";

import { MetaWrapper, NotFound } from "../../components/legacy";
import { PRODUCT_PAGE_TITLE_RULES } from "./constants";
import Page from "./Page";
import { IProps } from "./types";

const canDisplay = (product?: ProductDetails | null) =>
  !!product?.name && !!product?.pricing && !!product?.variants;

const extractMeta = (
  product?: ProductDetails | null,
  canonicalUrl?: string
) => ({
  custom: [
    {
      content: product?.pricing?.priceRange?.start?.gross?.amount?.toString(),
      property: "product:price:amount",
    },
    {
      content: product?.pricing?.priceRange?.start?.gross?.currency,
      property: "product:price:currency",
    },
    {
      content: product?.isAvailable ? "in stock" : "out off stock",
      property: "product:isAvailable",
    },
    {
      content: product?.category?.name,
      property: "product:category",
    },
  ],
  description:
    product?.seoDescription || extractSeoDescription(product?.description),
  lender: product?.lender,
  image: product?.thumbnail2x?.url,
  title: applyStringTransformRules(
    product?.name?.trim() || "",
    PRODUCT_PAGE_TITLE_RULES
  ),
  type: "product.item",
  url: ssrMode ? global.location.href : window.location.href,
  canonicalUrl,
});

// Component for rendering our Product Display
const PageWithQueryAttributes: React.FC<IProps> = (props) => (
  <Page {...props} />
);

export type ProductPageProps = {
  params: { slug: string } | undefined;
  data: ProductDetails | undefined | null;
  showRecommendations: boolean;
};

export const ProductPage: NextPage<ProductPageProps> = ({
  data: product,
  showRecommendations,
}) => {
  const { addItemToCheckout, items, removeItem, loaded } = useCart();
  const { user } = useAuth();
  const { login } = useLogin();
  const { clientIpAddress, trackProduct } = useTikTokPixelContext();
  const router = useRouter();
  const { push } = useDatalayer();

  useEffect(() => {
    if (product && clientIpAddress) {
      trackProduct("ViewContent", product);
    }
  }, [product, clientIpAddress]);

  const canEdit =
    product?.id &&
    user &&
    (user.isStaff || product?.lender?.user?.id === user.id);
  const editLink = canEdit ? <EditProductLink productId={product.id} /> : <></>;
  const canDisplayProduct = canDisplay(product);

  useEffect(() => {
    if (canDisplayProduct && product) {
      sendFacebookViewContentEvent(product, user);
    }
  }, [canDisplayProduct, product]);

  useEffect(() => {
    if (canDisplayProduct && product) {
      const selectedVariant = product.variants?.[0];
      const variantPrice =
        (selectedVariant?.pricing?.price?.gross?.amount || 0) +
        (selectedVariant?.pricing?.borrowerFee?.gross?.amount || 0);

      push({
        event: "view_item",
        ecommerce: {
          currency: selectedVariant?.pricing?.price?.gross?.currency || "",
          value: variantPrice,
          items: [
            {
              item_id: decodeBase64Id(product.id),
              item_name: product.name,
              item_brand: getAttributeValue("designer", product.attributes),
              item_category: product.category?.name || "",
              item_variant: `Size ${getAttributeValue(
                "size",
                product.attributes
              )}`,
              price: variantPrice,
              quantity: 1,
            },
          ],
        },
      });
    }
  }, [canDisplayProduct, product]);

  const addItemToCheckoutWithFacebookEvent = (
    params: ISelectedProductInput
  ):
    | Promise<{
        pending: boolean;
      }>
    | undefined => {
    if (product) {
      sendFacebookAddToCartEvent(product, params.variantId, user);
      return addItemToCheckout(params);
    }
  };

  const [isInWishlist, setIsInWishlist] = useState<boolean>(() => {
    if (
      user?.wishlist?.edges &&
      product &&
      user.wishlist.edges.some((item) => item.node.productId === product.id)
    ) {
      return true;
    }
    return false;
  });

  const [
    addProductToWishlist,
    {
      loading: addToWishlistLoading,
      // error: addToWishlistError
    },
    // @ts-ignore
  ] = useAddProductToWishlist(null, { refetchQueries: ["UserDetails"] });

  const [
    removeProductFromWishlist,
    {
      loading: removeFromWishLoading,
      // error: removeFromWishlistError
    },
    // @ts-ignore
  ] = useRemoveProductFromWishlist(null, { refetchQueries: ["UserDetails"] });

  const wishListLoading = addToWishlistLoading || removeFromWishLoading;

  const toggleWishlist = () => {
    if (user?.id && product) {
      if (isInWishlist) {
        if (!wishListLoading) {
          removeProductFromWishlist({ productId: product.id });
          setIsInWishlist(false);
        }
      } else if (!wishListLoading) {
        trackProduct("AddToWishlist", product, true);
        addProductToWishlist({
          productId: product.id,
          productSlug: product.slug,
        });
        setIsInWishlist(true);
      }
    } else {
      login({ addToWishlist: "true" });
    }
  };

  useEffect(() => {
    if (
      user?.id &&
      router.query.addToWishlist === "true" &&
      !wishListLoading &&
      product
    ) {
      addProductToWishlist({
        productId: product.id,
        productSlug: product.slug,
      });
      setIsInWishlist(true);
    }
  }, [user, router.query]);

  const { pathname } = router;
  const productSlug = product?.slug || "";
  const basePath = `${siteUrl}${pathname}`;
  const canonicalUrl = basePath.replace(/\[slug\]/g, productSlug);

  if (!product || isEmpty(product)) {
    return <NotFound />;
  }

  return (
    <MetaWrapper meta={extractMeta(product, canonicalUrl)}>
      <PageWithQueryAttributes
        product={product}
        add={addItemToCheckoutWithFacebookEvent}
        items={items}
        editLink={editLink}
        toggleWishlist={toggleWishlist}
        isInWishlist={isInWishlist}
        user={user}
        removeItem={removeItem}
        showRecommendations={showRecommendations}
        loaded={loaded}
      />
    </MetaWrapper>
  );
};
